import pick from 'lodash/pick';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { transactionLineItems } from '../../util/api';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import { findNextBoundary, getStartOf, monthIdString } from '../../util/dates';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import { getProcess } from '../../transactions/transaction';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';
import { isVariantsInUse } from '../../util/search';
import { getOfferTransitions } from '../../transactions/transactionHelpers';
import { defaultLocale } from '../../intl';
import { getBrandingData } from '../../util/branding';
import { isRefurbishedListing } from '../../util/genericHelpers';
import { getConversionRates, getConversionRatesOffer } from '../../util/api';
import { currenciesHelper as currency } from '../../helpers/currencies';
import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { ASSET_NAME } from '../../containers/RefundAndReturnPolicyPage/RefundAndReturnPolicyContent';

const { UUID } = sdkTypes;
const brandId = process.env.REACT_APP_BRAND_NAME;
const marketplaceUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL;
const REACT_APP_CURRENCY_SWITCH_ENABLED = process.env.REACT_APP_CURRENCY_SWITCH_ENABLED === 'true';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ListingPage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';

export const FETCH_TIME_SLOTS_REQUEST = 'app/ListingPage/FETCH_TIME_SLOTS_REQUEST';
export const FETCH_TIME_SLOTS_SUCCESS = 'app/ListingPage/FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_TIME_SLOTS_ERROR = 'app/ListingPage/FETCH_TIME_SLOTS_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/ListingPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/ListingPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/ListingPage/FETCH_LINE_ITEMS_ERROR';

export const SEND_INQUIRY_REQUEST = 'app/ListingPage/SEND_INQUIRY_REQUEST';
export const SEND_INQUIRY_SUCCESS = 'app/ListingPage/SEND_INQUIRY_SUCCESS';
export const SEND_INQUIRY_ERROR = 'app/ListingPage/SEND_INQUIRY_ERROR';

export const SEARCH_VARIANT_LISTINGS_SUCCESS = 'app/ListingPage/SEARCH_VARIANT_LISTINGS_SUCCESS';
export const SEARCH_VARIANT_LISTINGS_ERROR = 'app/ListingPage/SEARCH_VARIANT_LISTINGS_ERROR';

export const FETCH_CONVERSION_RATES = 'app/ListingPage/FETCH_CONVERSION_RATES';
export const FETCH_CONVERSION_RATES_OFFER = 'app/ListingPage/FETCH_CONVERSION_RATES_OFFER';

// ================ Reducer ================ //

const initialState = {
  id: null,
  showListingError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendInquiryInProgress: false,
  sendInquiryError: null,
  inquiryModalOpenForListingId: null,
  variantListingIds: [],
  conversionRates: null,
  conversionRatesOffer: null,
};

const resultIds = data => data.data?.map(l => l?.id);

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SEARCH_VARIANT_LISTINGS_SUCCESS:
      return {
        ...state,
        variantListingIds: resultIds(payload.data),
      };

    case SEARCH_VARIANT_LISTINGS_ERROR:
      return { ...state, variantListingIds: [] };

    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };

    case FETCH_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_SUCCESS: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_ERROR: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case SEND_INQUIRY_REQUEST:
      return { ...state, sendInquiryInProgress: true, sendInquiryError: null };
    case SEND_INQUIRY_SUCCESS:
      return { ...state, sendInquiryInProgress: false };
    case SEND_INQUIRY_ERROR:
      return { ...state, sendInquiryInProgress: false, sendInquiryError: payload };

    case FETCH_CONVERSION_RATES:
      return { ...state, conversionRates: payload };

    case FETCH_CONVERSION_RATES_OFFER:
      return { ...state, conversionRatesOffer: payload };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const fetchTimeSlotsRequest = monthId => ({
  type: FETCH_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchTimeSlotsError = (monthId, error) => ({
  type: FETCH_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const sendInquiryRequest = () => ({ type: SEND_INQUIRY_REQUEST });
export const sendInquirySuccess = () => ({ type: SEND_INQUIRY_SUCCESS });
export const sendInquiryError = e => ({ type: SEND_INQUIRY_ERROR, error: true, payload: e });

export const searchListingsSuccess = response => ({
  type: SEARCH_VARIANT_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_VARIANT_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchConversionRates = payload => ({
  type: FETCH_CONVERSION_RATES,
  payload,
});

export const fetchConversionRatesOffer = payload => ({
  type: FETCH_CONVERSION_RATES_OFFER,
  payload,
});

// ================ Thunks ================ //

export const queryGCProductListings = (gtProductId, includedParams) => async (
  dispatch,
  getState,
  sdk
) => {
  try {
    const response = await sdk.listings.query({
      ...includedParams,
      pub_gtProductId: gtProductId,
      minStock: 1,
    });

    dispatch(addMarketplaceEntities(response));
    dispatch(searchListingsSuccess(response));
    return response;
  } catch (error) {
    dispatch(showListingError(error));
  }
};

export const showListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  dispatch(showListingRequest(listingId));
  dispatch(fetchCurrentUser());

  const isVariantsEnabled = isVariantsInUse(config);

  const includedParams = {
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  const params = {
    id: listingId,
    ...includedParams,
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      const publicData = data?.data?.data?.attributes?.publicData;

      const brandIdListing = publicData?.brandId;
      const gtProductId = publicData?.gtProductId;

      // Refurbished listings only redirect to the brand webshop, so they never
      // show a variant picker and must not be grouped with user listings.
      const isRefurbished = isRefurbishedListing(data?.data?.data);
      if (isVariantsEnabled && gtProductId && !isRefurbished) {
        dispatch(queryGCProductListings(gtProductId, includedParams));
      }

      // Check if the listing's brandId matches the expected brandId
      if (brandIdListing !== brandId) {
        // If not, dispatch an error and throw to stop execution
        const error = new Error(
          `Listing brandId (${brandIdListing}) does not match the brandId (${brandId}).`
        );
        dispatch(showListingError(storableError(error)));
        throw error;
      }
      dispatch(addMarketplaceEntities(data, sanitizeConfig));
      return data;
    })
    .catch(e => {
      dispatch(showListingError(storableError(e)));
    });
};

const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then(response => {
    return denormalisedResponseEntities(response);
  });
};

export const fetchTimeSlots = (listingId, start, end, timeZone) => (dispatch, getState, sdk) => {
  const monthId = monthIdString(start, timeZone);

  dispatch(fetchTimeSlotsRequest(monthId));

  // The maximum pagination page size for timeSlots is 500
  const extraParams = {
    perPage: 500,
    page: 1,
  };

  return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
    .then(timeSlots => {
      dispatch(fetchTimeSlotsSuccess(monthId, timeSlots));
    })
    .catch(e => {
      dispatch(fetchTimeSlotsError(monthId, storableError(e)));
    });
};

export const fetchRatesOffer = currency => async (dispatch, getState, sdk) => {
  if (REACT_APP_CURRENCY_SWITCH_ENABLED) {
    return getConversionRatesOffer({ offerCurrency: currency })
      .then(response => {
        dispatch(fetchConversionRatesOffer(response));
        return response;
      })
      .catch(e => {
        console.log(e);
      });
  } else {
    return null;
  }
};

export const sendInquiry = (listing, message, offerParams) => (dispatch, getState, sdk) => {
  dispatch(sendInquiryRequest());

  const { variant, currentLocale, currentUser, ...rest } = offerParams || {};

  const offerPrice = offerParams?.offerPrice;
  const currentCurrency = offerParams?.currency;
  const listingCurrency = offerParams?.listingCurrency;

  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(sendInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const listingId = listing?.id;
  const [processName, alias] = processAlias.split('/');
  const transitions = getProcess(processName)?.transitions;

  const providerLanguage =
    listing?.author?.attributes?.profile?.publicData?.language || defaultLocale;
  const customerLanguage = currentUser?.attributes?.profile?.publicData?.language || defaultLocale;

  const brandingData = getBrandingData();

  const params = offerPrice
    ? {
        listingId,
        protectedData: {
          offerParams: rest,
          ...brandingData,
          brandName: brandId,
          brandUrl: marketplaceUrl,
          providerLanguage,
          customerLanguage,
          isProviderEn: providerLanguage === 'en' ? true : null,
          isProviderFr: providerLanguage === 'fr' ? true : null,
          isProviderDe: providerLanguage === 'de' ? true : null,
          isProviderIt: providerLanguage === 'it' ? true : null,
          isProviderEs: providerLanguage === 'es' ? true : null,

          isCustomerEn: customerLanguage === 'en' ? true : null,
          isCustomerFr: customerLanguage === 'fr' ? true : null,
          isCustomerDe: customerLanguage === 'de' ? true : null,
          isCustomerIt: customerLanguage === 'it' ? true : null,
          isCustomerEs: customerLanguage === 'es' ? true : null,

          currentCurrency,
          listingCurrency,
        },
      }
    : {
        listingId,
        protectedData: {
          ...brandingData,
          brandName: brandId,
          brandUrl: marketplaceUrl,
          providerLanguage,
          customerLanguage,
          isProviderEn: providerLanguage === 'en' ? true : null,
          isProviderFr: providerLanguage === 'fr' ? true : null,
          isProviderDe: providerLanguage === 'de' ? true : null,
          isProviderIt: providerLanguage === 'it' ? true : null,
          isProviderEs: providerLanguage === 'es' ? true : null,

          isCustomerEn: customerLanguage === 'en' ? true : null,
          isCustomerFr: customerLanguage === 'fr' ? true : null,
          isCustomerDe: customerLanguage === 'de' ? true : null,
          isCustomerIt: customerLanguage === 'it' ? true : null,
          isCustomerEs: customerLanguage === 'es' ? true : null,

          currentCurrency,
          listingCurrency,
        },
      };

  const bodyParams = {
    transition: offerPrice ? getOfferTransitions().TRANSITION_SEND_OFFER : transitions.INQUIRE,
    processAlias,
    params,
  };
  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;

      // If offerPrice is provided, return transactionId immediately
      if (offerPrice) {
        dispatch(sendInquirySuccess());
        dispatch(fetchCurrentUserHasOrdersSuccess(true));
        return transactionId;
      }

      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(sendInquirySuccess());
        dispatch(fetchCurrentUserHasOrdersSuccess(true));
        return transactionId;
      });
    })
    .catch(e => {
      dispatch(sendInquiryError(storableError(e)));
      throw e;
    });
};

export const fetchTransactionLineItems = ({
  orderData,
  listingId,
  isOwnListing,
  currentCurrency,
  listingCurrency,
}) => dispatch => {
  dispatch(fetchLineItemsRequest());
  transactionLineItems({ orderData, listingId, isOwnListing, currentCurrency, listingCurrency })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
      });
    });
};

export const loadData = (params, search, config) => async dispatch => {
  const listingId = new UUID(params.id);

  // Clear old line-items
  dispatch(setInitialValues({ lineItems: null }));

  // Fetch refund policy
  const refundPolicyAsset = {
    refundAndReturnPolicy: `content/pages/${ASSET_NAME}.json`,
  };
  dispatch(fetchPageAssets(refundPolicyAsset, true));

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, config, true));
  }

  if (REACT_APP_CURRENCY_SWITCH_ENABLED) {
    await getConversionRates({})
      .then(response => {
        dispatch(fetchConversionRates(response));
        return response;
      })
      .catch(e => {
        console.log(e);
      });
  }

  return Promise.all([dispatch(showListing(listingId, config))]).then(response => {
    const listing = response[0]?.data?.data;
    const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
    return response;
  });
};
